import { useCallback, useEffect, useRef, useState } from 'react';
import { Input } from '@/components/ui/input';
import { useGoogleMaps } from '@/hooks/useGoogleMaps';
import { Pencil } from 'lucide-react';

export interface AddressWithCoordinates {
  address: string;
  latitude?: number;
  longitude?: number;
}

interface AddressAutocompleteProps {
  id?: string;
  placeholder?: string;
  value: string;
  onChange: (value: string, coordinates?: { latitude: number; longitude: number }) => void;
  rows?: number;
  required?: boolean;
  className?: string;
}

export function AddressAutocomplete({
  id,
  placeholder,
  value,
  onChange,
  required = false,
  className,
}: AddressAutocompleteProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const autocompleteService = useRef<google.maps.places.AutocompleteService | null>(null);
  const placesService = useRef<google.maps.places.PlacesService | null>(null);
  const debounceTimer = useRef<NodeJS.Timeout | null>(null);

  const { isLoaded, error } = useGoogleMaps();
  const [isEditing, setIsEditing] = useState(!value);
  const [query, setQuery] = useState('');
  const [predictions, setPredictions] = useState<google.maps.places.AutocompletePrediction[]>([]);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [showDropdown, setShowDropdown] = useState(false);

  // Initialize services once
  useEffect(() => {
    if (!isLoaded || error) return;
    autocompleteService.current = new google.maps.places.AutocompleteService();
    // PlacesService needs a DOM element (hidden div works fine)
    const div = document.createElement('div');
    placesService.current = new google.maps.places.PlacesService(div);
  }, [isLoaded, error]);

  // Fetch predictions when query changes
  const fetchPredictions = useCallback((input: string) => {
    if (!autocompleteService.current || !input.trim()) {
      setPredictions([]);
      setShowDropdown(false);
      return;
    }

    autocompleteService.current.getPlacePredictions(
      { input, types: ['address'] },
      (results, status) => {
        if (status === google.maps.places.PlacesServiceStatus.OK && results) {
          setPredictions(results);
          setShowDropdown(true);
        } else {
          setPredictions([]);
          setShowDropdown(false);
        }
      },
    );
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setQuery(val);
    setHighlightedIndex(-1);

    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => fetchPredictions(val), 200);
  };

  const selectPrediction = useCallback((prediction: google.maps.places.AutocompletePrediction) => {
    if (!placesService.current) return;

    placesService.current.getDetails(
      {
        placeId: prediction.place_id,
        fields: ['address_components', 'formatted_address', 'geometry'],
      },
      (place, status) => {
        if (status !== google.maps.places.PlacesServiceStatus.OK || !place?.formatted_address) return;

        const addressComponents = place.address_components || [];
        const streetNumber = addressComponents.find((c) => c.types.includes('street_number'))?.long_name || '';
        const route = addressComponents.find((c) => c.types.includes('route'))?.long_name || '';
        const city = addressComponents.find((c) => c.types.includes('locality'))?.long_name || '';
        const state = addressComponents.find((c) => c.types.includes('administrative_area_level_1'))?.short_name || '';
        const zip = addressComponents.find((c) => c.types.includes('postal_code'))?.long_name || '';

        const line1 = `${streetNumber} ${route}`.trim();
        const line2 = `${city}, ${state} ${zip}`.trim();
        const formattedAddress = line1 && line2 ? `${line1}\n${line2}` : place.formatted_address;

        const coordinates = place.geometry?.location
          ? {
              latitude: place.geometry.location.lat(),
              longitude: place.geometry.location.lng(),
            }
          : undefined;

        onChange(formattedAddress, coordinates);
        setIsEditing(false);
        setQuery('');
        setPredictions([]);
        setShowDropdown(false);
        setHighlightedIndex(-1);
      },
    );
  }, [onChange]);

  const scrollToHighlighted = (index: number) => {
    const container = dropdownRef.current;
    if (!container) return;
    const items = container.children;
    if (items[index]) {
      (items[index] as HTMLElement).scrollIntoView({ block: 'nearest' });
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!showDropdown || predictions.length === 0) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      const next = (highlightedIndex + 1) % predictions.length;
      setHighlightedIndex(next);
      scrollToHighlighted(next);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      const next = highlightedIndex <= 0 ? predictions.length - 1 : highlightedIndex - 1;
      setHighlightedIndex(next);
      scrollToHighlighted(next);
    } else if (e.key === 'Enter' && highlightedIndex >= 0) {
      e.preventDefault();
      selectPrediction(predictions[highlightedIndex]);
    } else if (e.key === 'Escape') {
      setShowDropdown(false);
      setHighlightedIndex(-1);
    }
  };

  // Close dropdown on outside click
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(e.target as Node)
      ) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
    };
  }, []);

  // If Google Maps fails to load, fallback to plain input
  if (error) {
    return (
      <Input
        id={id}
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        className={className}
        autoComplete="off"
        data-1p-ignore
      />
    );
  }

  // Display card when address is set
  if (value && !isEditing) {
    return (
      <div className="flex items-center gap-3 p-3.5 bg-primary/5 border border-primary/20 rounded-xl">
        <span className="text-sm font-medium flex-1 whitespace-pre-line leading-relaxed">
          {value}
        </span>
        <button
          type="button"
          onClick={() => {
            onChange('');
            setIsEditing(true);
            setTimeout(() => inputRef.current?.focus(), 0);
          }}
          className="text-muted-foreground hover:text-foreground transition-colors p-1 rounded-lg hover:bg-muted"
        >
          <Pencil className="h-3.5 w-3.5" />
        </button>
      </div>
    );
  }

  // Autocomplete input with our own dropdown
  return (
    <div className="relative">
      <Input
        ref={inputRef}
        id={id}
        placeholder={placeholder || 'Start typing an address...'}
        value={query}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onFocus={() => { if (predictions.length > 0) setShowDropdown(true); }}
        required={required}
        className={className}
        autoComplete="off"
        data-1p-ignore
      />

      {showDropdown && predictions.length > 0 && (
        <div
          ref={dropdownRef}
          className="absolute z-20 left-0 right-0 top-full mt-1 max-h-48 overflow-y-auto border rounded-xl divide-y bg-background shadow-lg scrollbar-thin"
        >
          {predictions.map((pred, i) => (
            <button
              type="button"
              key={pred.place_id}
              onClick={() => selectPrediction(pred)}
              onMouseEnter={() => setHighlightedIndex(i)}
              className={`w-full text-left px-4 py-3 text-sm transition-colors ${
                i === highlightedIndex ? 'bg-muted' : 'hover:bg-muted'
              }`}
            >
              <span className="font-medium">
                {pred.structured_formatting.main_text}
              </span>{' '}
              <span className="text-muted-foreground">
                {pred.structured_formatting.secondary_text}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
